import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { DailyTable } from "@/features/daily/components/DailyTable";
import { dailyFormat } from "@/features/daily/components/command/command-view-model";
import type { TodayHourVm, TodayHourlyVm } from "@/features/daily/components/command/today-view-model";

type Palette = { blue: string; violet: string; green: string; muted: string; soft: string; surface: string; grid: string; text: string; fontSize: number };

/** Canvas needs literal colours: read the page's own tokens off the chart's box. */
const readPalette = (node: HTMLElement): Palette => {
    const style = getComputedStyle(node);
    const token = (name: string): string => style.getPropertyValue(name).trim();
    const fontSize = Number.parseFloat(token("--daily-text-xs"));
    return {
        blue: token("--daily-blue"),
        violet: token("--daily-violet"),
        green: token("--daily-green"),
        muted: token("--daily-muted"),
        soft: token("--daily-soft"),
        surface: token("--daily-surface"),
        grid: token("--daily-line"),
        text: token("--daily-muted"),
        fontSize: Number.isNaN(fontSize) ? 11 : fontSize,
    };
};

/**
 * D134 + D136: today hour by hour — created orders and deposits as stacked bars (blue and
 * violet, validated as a categorical pair), completed operations as a line, the baseline's
 * expectation dashed, quiet slots shaded. Everything drawn is in the table for screen
 * readers (WCAG 1.1.1); the canvas fills its box and follows it (D132).
 */
export const TodayHourlyCard: React.FC<{ vm: TodayHourlyVm }> = ({ vm }) => {
    const chartRef = useRef<HTMLDivElement | null>(null);
    const [palette, setPalette] = useState<Palette | null>(null);

    useEffect(() => {
        const node = chartRef.current;
        if (node === null) return undefined;
        const update = (): void => setPalette(readPalette(node));
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    const option = useMemo(() => {
        if (palette === null) return null;
        const axisLabel = { color: palette.text, fontSize: palette.fontSize };
        const labels = vm.hours.map((hour) => hour.label);
        const quietArea = {
            silent: true,
            itemStyle: { color: palette.soft },
            data: vm.hours.filter((hour) => hour.quiet).map((hour) => [{ xAxis: hour.label }, { xAxis: hour.label }]),
        };
        return {
            animation: false,
            grid: { left: 8, right: 8, top: 28, bottom: 8, containLabel: true },
            legend: { top: 0, right: 0, itemWidth: 14, itemHeight: 8, textStyle: axisLabel },
            tooltip: { trigger: "axis" },
            xAxis: { type: "category", data: labels, axisLabel, axisLine: { lineStyle: { color: palette.grid } }, axisTick: { show: false } },
            yAxis: { type: "value", minInterval: 1, axisLabel, splitLine: { lineStyle: { color: palette.grid, type: "dashed" } } },
            series: [
                // D136: created split by type, stacked, a 1 px surface border between segments.
                // D139: a single «Creadas» series when only one type was created today.
                ...(vm.byType ? [
                    { name: "Órdenes creadas", type: "bar", stack: "created", data: vm.hours.map((hour) => hour.createdOrders), itemStyle: { color: palette.blue, borderColor: palette.surface, borderWidth: 1 }, barMaxWidth: 18, markArea: quietArea },
                    { name: "Depósitos creados", type: "bar", stack: "created", data: vm.hours.map((hour) => hour.createdDeposits), itemStyle: { color: palette.violet, borderColor: palette.surface, borderWidth: 1, borderRadius: [3, 3, 0, 0] }, barMaxWidth: 18 },
                ] : [
                    { name: "Creadas", type: "bar", data: vm.hours.map((hour) => hour.created), itemStyle: { color: palette.blue, borderRadius: [3, 3, 0, 0] }, barMaxWidth: 18, markArea: quietArea },
                ]),
                { name: "Completadas", type: "line", data: vm.hours.map((hour) => hour.completed), showSymbol: false, lineStyle: { color: palette.green, width: 2 }, itemStyle: { color: palette.green } },
                ...(vm.baselineNote === null ? [{
                    name: "Esperadas",
                    type: "line",
                    data: vm.hours.map((hour) => hour.expected),
                    showSymbol: false,
                    connectNulls: false,
                    lineStyle: { color: palette.muted, width: 2, type: "dashed" },
                    itemStyle: { color: palette.muted },
                }] : []),
            ],
        };
    }, [palette, vm]);

    return (
        <section className="daily-panel daily-today-card daily-hourly" aria-labelledby="daily-hourly-title">
            <div className="daily-panel__head">
                <h2 id="daily-hourly-title">Actividad de hoy por hora</h2>
                {vm.baselineNote === null ? null : <span className="daily-small daily-muted">{vm.baselineNote}</span>}
            </div>
            <div ref={chartRef} className="daily-chart daily-hourly__chart" role="img" aria-label="Operaciones creadas y completadas por hora frente a lo esperado. Los datos están en la tabla.">
                {option === null ? null : (
                    <ReactECharts option={option} notMerge autoResize style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
                )}
            </div>
            <div className="daily-visually-hidden">
                <DailyTable
                    caption="Actividad de hoy por hora"
                    columns={[
                        { key: "hour", header: "Hora", render: (hour: TodayHourVm) => `${hour.label}${hour.quiet ? " · tranquila" : ""}` },
                        ...(vm.byType ? [
                            { key: "orders", header: "Órdenes creadas", numeric: true, render: (hour: TodayHourVm) => dailyFormat.exactCount(hour.createdOrders) },
                            { key: "deposits", header: "Depósitos creados", numeric: true, render: (hour: TodayHourVm) => dailyFormat.exactCount(hour.createdDeposits) },
                        ] : [
                            { key: "created", header: "Creadas", numeric: true, render: (hour: TodayHourVm) => dailyFormat.exactCount(hour.created) },
                        ]),
                        { key: "completed", header: "Completadas", numeric: true, render: (hour: TodayHourVm) => dailyFormat.exactCount(hour.completed) },
                        { key: "expected", header: "Esperadas", numeric: true, render: (hour: TodayHourVm) => (hour.expected === null ? dailyFormat.absent : dailyFormat.exactCount(Math.round(hour.expected))) },
                    ]}
                    rows={vm.hours}
                    rowKey={(hour) => String(hour.hour)}
                    emptyMessage="Aún no ha transcurrido ninguna hora de hoy."
                />
            </div>
        </section>
    );
};
